{ "name": "BaseUpload", "private": true, "type": "module", "scripts": { // ...
existing code ... }, "devDependencies": { // ... existing code ... },
"dependencies": { // ... existing code ... } }

<script setup lang="ts">
import { PropType, ref, computed } from 'vue';
import { useApply } from '~/store/useApply';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { LANG } from '~/config/lang';
import { useApp } from '~/store/useApp';
const appStore = useApp();

const props = defineProps({
  name: {
    type: String as PropType<string>,
    required: true,
  },
  title: {
    type: String as PropType<string>,
    required: false,
    default: '',
  },
  validate: null,
  maxFileSize: {
    type: Number as PropType<number>,
    default: 10, // 10MB
  },
  allowedFileTypes: {
    type: Array as PropType<string[]>,
    default: () => ['.pdf', '.doc', '.docx', '.txt'],
  },
  maxFiles: {
    type: Number,
    default: 1,
  },
  isMultiple: {
    type: Boolean,
    default: false,
  },
  maxFilesSize: {
    type: Number,
    default: 20, // 20MB
  },
});

const emit = defineEmits({
  change: null,
});

const files = ref<File[]>([]);
const error = ref<string>();

// Add computed property for total file size
const totalFilesSize = computed(() => {
  const totalBytes = files.value.reduce((total, file) => total + file.size, 0);
  const totalMB = totalBytes / (1024 * 1024);
  return totalMB.toFixed(2);
});

function validateFile(file: File): string | null {
  // Check file size (convert maxFileSize from MB to bytes)
  if (file.size > props.maxFileSize * 1024 * 1024) {
    return `File ${file.name} is too large. Maximum size is ${props.maxFileSize}MB`;
  }

  // Check file type
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!props.allowedFileTypes.includes(fileExtension)) {
    return `File ${
      file.name
    } type not allowed. Allowed types: ${props.allowedFileTypes.join(', ')}`;
  }

  return null;
}

function changeHandle(e: any) {
  const newFiles = Array.from(e.target.files) as File[];
  if (newFiles.length === 0) {
    return;
  }
  error.value = '';

  // Calculate current total size of all files
  const currentTotalSize = files.value.reduce(
    (total, file) => total + file.size,
    0
  );

  console.log(`Current total size: ${currentTotalSize} bytes`);

  newFiles.forEach((file: File) => {
    if (files.value.length >= props.maxFiles) {
      error.value = props.isMultiple
        ? `${LANG.maxFilesError?.[appStore.lang]} ${props.maxFiles}.`
        : `${LANG.onlyOneFileError?.[appStore.lang]}`;
      emit('change', files.value);
      return;
    } else {
      error.value = '';
    }

    const err = validateFile(file);
    if (err) {
      error.value = err;
      emit('change', files.value);
      return;
    }

    // Check if adding this file would exceed the total size limit
    const newTotalSize = currentTotalSize + file.size;
    console.log(`New file size: ${file.size} bytes`);
    console.log(`New total size after adding file: ${newTotalSize} bytes`);
    if (newTotalSize > props.maxFilesSize * 1024 * 1024) {
      error.value = `${LANG.maxFilesSizeError?.[appStore.lang]} ${
        props.maxFilesSize
      }MB`;
      emit('change', files.value);
      return;
    }

    if (!files.value.find((f) => f.name === file.name)) {
      files.value.push(file);
    }
  });

  emit('change', files.value);
}

function removeFile(index: number) {
  files.value.splice(index, 1);
  error.value =
    files.value.length >= props.maxFiles
      ? `${LANG.maxFilesError?.[appStore.lang]} ${props.maxFiles}.`
      : '';
  emit('change', files.value);
}

const store = useApply();
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-1 items-center">
      <label
        :for="name"
        :class="[
          'file-upload relative w-[56px] h-[56px] bg-white rounded-[5px] shadow-md flex justify-center items-center cursor-pointer',
          validate ? 'outline outline-2 outline-red-500' : '',
        ]"
        role="button"
        aria-label="Upload files"
      >
        <img src="~/assets/upload.jpg" alt="upload" class="w-[22px] h-[22px]" />
        <input
          type="file"
          :name="name"
          :id="name"
          class="w-0 focus:outline-none"
          @change="changeHandle"
          multiple
          :accept="allowedFileTypes.join(',')"
        />
      </label>
      <label class="text-[20px] ml-[11px] -mt-5 mr-[11px] text-dark">
        {{ title }}
      </label>
    </div>

    <transition name="slide-down">
      <div
        v-if="error"
        class="text-red-600 text-sm bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2 shadow-sm animate-slide"
        style="min-height: 40px"
      >
        <svg
          class="w-5 h-5 text-red-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 8v4m0 4h.01"
          />
        </svg>
        <p class="font-medium">{{ error }}</p>
      </div>
    </transition>

    <transition-group
      name="slide-file"
      tag="div"
      v-if="files.length > 0"
      class="flex flex-col gap-2"
    >
      <div
        v-for="(file, index) in files"
        :key="file.name"
        class="flex items-center justify-between bg-blue-50 border border-blue-200 rounded px-3 py-2 shadow-sm group animate-slide"
        style="min-height: 40px"
      >
        <span
          class="text-sm text-blue-700 font-medium flex items-center gap-2"
          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
        >
          <svg
            class="w-5 h-5 text-blue-400"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              d="M4 4a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4z"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
            />
            <polyline
              points="14 2 14 8 20 8"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
            />
          </svg>
          {{ file.name }}
        </span>
        <button
          @click="removeFile(index)"
          class="p-1 rounded-full hover:bg-gray-200 transition-colors"
          type="button"
          aria-label="Remove file"
        >
          <XMarkIcon class="h-5 w-5 text-gray-500 hover:text-red-500" />
        </button>
      </div>
    </transition-group>

    <!-- Add total files size display -->
    <transition name="slide-down">
      <div
        v-if="files.length > 0"
        class="text-gray-400 text-sm border border-gray-200 rounded px-3 py-2 flex items-center justify-between"
      >
        <span class="font-medium">{{ LANG.totalSize?.[appStore.lang] }}:</span>
        <span class="text-gray-500"
          >{{ totalFilesSize }}MB / {{ maxFilesSize }}MB</span
        >
      </div>
    </transition>
  </div>
</template>

<style scoped>
.file-upload:focus-within {
  outline: 1px solid black;
}

/* Slide down/up transition for error message */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}
.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 100px;
  transform: translateY(0);
}

/* Smooth slide animation for error and files (less jumpy) */
.animate-slide {
  animation: slide-in 0.3s;
}
@keyframes slide-in {
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Smoother slide in/out transition for file items (no bounce) */
.slide-file-enter-active,
.slide-file-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}
.slide-file-enter-from,
.slide-file-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
.slide-file-enter-to,
.slide-file-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
